import { Box, css, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { CreatePaymentPlanDto } from 'src/api/paymentPlanService/dto/CreatePaymentPlanDto';
import { ReactComponent as MinusIcon } from 'src/assets/svg/minus.svg';
import { ReactComponent as PlusIcon } from 'src/assets/svg/plus.svg';
import { Section } from 'src/components/section';
import { Select } from 'src/components/select';
import { useCreatePaymentPlan } from 'src/lib/hooks/use-create-payment-plan';
import { Currency } from 'src/shared/enum/Currency';
import { ErrorMessage } from 'src/shared/enum/ErrorMessage';
import { InstalmentType } from 'src/shared/enum/InstalmentType';
import { selectRoom } from 'src/store/slices/booking-slice';
import {
    selectUserChoices,
    setCreatedPaymentPlan,
    setInstalmentId,
    setInstalmentType,
    setNumberOfStudents,
    setTerm,
} from 'src/store/slices/payment-plan-slice';
import { Term } from 'src/store/types/booking-types';
import { getTheme } from 'src/utils/getTheme';
import { formatDate } from 'src/utils/format-date';
import { getCurrencySymbol } from 'src/utils/get-currency-symbol';
import { createLabelFromInstalmentType } from 'src/utils/instalment-type-to-name-convert';
import { getStorage, setPaymentPlanSelectionStorage } from 'src/utils/local-storage-versioning';

import {
    updateBasketProductPaymentplanId,
    updateBasketRoommates,
} from '../../../store/slices/basket-slice';

import { styles } from './styles';

import type { BookingDetailsProps } from './types';

export const BookingDetails: FC<BookingDetailsProps> = ({
    restStyles,
    bookingDetails,
    promotionId,
    isRebooker,
    isFillerWeekEligible,
    setButtonDisabled,
}) => {
    const { t } = useTranslation();
    const [maxStudentNumber, setMaxStudentNumber] = useState<number>(1);
    const [minStudentNumber, setMinStudentNumber] = useState<number>(1);
    const { numberOfStudents, term: selectedTerm, instalmentType } = useSelector(selectUserChoices);
    const dispatch = useDispatch();
    const roomData = useSelector(selectRoom);
    const { theme, isHosTheme } = getTheme();
    const storage = getStorage();
    const { paymentPlanId = null } = storage.redirectState;

    const termName = selectedTerm && selectedTerm.name;
    const monthlyPrice = roomData && roomData.monthly_price;
    const PRICE_PER_WEEK_LABEL = monthlyPrice
        ? t('booking_summary_per_month_payment')
        : t('booking_summary_per_week_payment');

    const { mutate: createPaymentPlan } = useCreatePaymentPlan();

    const postCreatePaymentPlan = () => {
        if (!bookingDetails) {
            return;
        }

        const selectedTermDetails = bookingDetails.terms.find((term) => term.name === termName);

        if (!selectedTermDetails) {
            return;
        }

        const selectedInstalment = selectedTermDetails.instalments.find(
            (instalment) => instalment.name === instalmentType
        );

        if (!roomData) throw new Error(ErrorMessage.NO_ROOM_PROVIDED);
        if (!selectedInstalment) throw new Error(ErrorMessage.NO_INSTALMENT_SELECTED);

        const payload = new CreatePaymentPlanDto({
            selectedStudents: numberOfStudents,
            instalmentType,
            selectedTerm: termName,
            instalmentId: selectedInstalment.id,
            academicYear: roomData.academic_year,
            roomCode: roomData.room_code,
            basePrice: roomData.base_price,
            promotionId,
            isRebooker,
            fillerWeekEligible: isFillerWeekEligible,
        });

        setButtonDisabled(true);

        createPaymentPlan(payload, {
            onSuccess(data) {
                setButtonDisabled(false);
                dispatch(setCreatedPaymentPlan(data));
                setPaymentPlanSelectionStorage({
                    createdPaymentPlan: data,
                    userChoices: {
                        numberOfStudents,
                        term: selectedTerm,
                        instalmentType,
                        instalmentId: selectedInstalment.id,
                    },
                });
            },
        });
    };

    useEffect(() => {
        if (bookingDetails) {
            const { terms, options } = bookingDetails;

            if (terms && terms.length > 0 && !selectedTerm) {
                dispatch(setTerm(terms[0]));
            }

            if (selectedTerm) {
                const selectedTermDetails = terms.find((term) => term.name === selectedTerm.name);

                if (selectedTermDetails) {
                    const instalmentsExist =
                        selectedTermDetails.instalments &&
                        selectedTermDetails.instalments.length > 0;

                    if (instalmentsExist && !instalmentType) {
                        dispatch(
                            setInstalmentType(
                                selectedTermDetails.instalments[0].name as InstalmentType
                            )
                        );
                        dispatch(setInstalmentId(selectedTermDetails.instalments[0].id));
                    } else if (!instalmentsExist) {
                        dispatch(setInstalmentType(null));
                        dispatch(setInstalmentId(null));
                    }
                } else {
                    dispatch(setTerm(terms[0]));
                    dispatch(setInstalmentType(terms[0].instalments[0].name as InstalmentType));
                    dispatch(setInstalmentId(terms[0].instalments[0].id));
                }
            }

            const { students } = options;
            if (students) {
                const { minStudents, maxStudents } = students;
                setMinStudentNumber(minStudents);
                setMaxStudentNumber(maxStudents);

                if (!numberOfStudents || numberOfStudents < minStudents) {
                    dispatch(setNumberOfStudents(minStudents));
                } else if (numberOfStudents > maxStudents) {
                    dispatch(setNumberOfStudents(maxStudents));
                }
            }

            if (selectedTerm && instalmentType && numberOfStudents) {
                postCreatePaymentPlan();
            }
        }
    }, [
        bookingDetails,
        selectedTerm,
        instalmentType,
        numberOfStudents,
        promotionId,
        isRebooker,
        isFillerWeekEligible,
    ]);

    const getTermTitle = (count: number) => {
        if (monthlyPrice && !paymentPlanId) {
            return t('booking_summary_months_count', {
                count,
            });
        }
        return t('booking_summary_weeks_count', {
            count,
        });
    };

    const getInstalmentOptions = () => {
        if (!bookingDetails) throw new Error(ErrorMessage.NO_BOOKING_DETAILS_PROVIDED);

        // Fetch the currency symbol using the CurrencySymbol mapping
        const currencySymbol = getCurrencySymbol(bookingDetails.currency as Currency);

        const selectedTermDetails = bookingDetails.terms.find((term) => term.name === termName);
        if (!selectedTermDetails || !selectedTermDetails.instalments) return [];

        return selectedTermDetails.instalments.map((instalment) => {
            const selectedAmount = instalment.amounts.find(
                (amt) => amt.students === numberOfStudents
            );
            const price = selectedAmount ? new Decimal(selectedAmount.price) : new Decimal(0);

            const instalmentLabel = createLabelFromInstalmentType(instalment.name, t);
            const pricePwLabel = PRICE_PER_WEEK_LABEL;

            return {
                id: instalment.id,
                value: instalment.name,
                title: `${currencySymbol}${price.toFixed(2)}${pricePwLabel}`,
                subtitle: instalmentLabel,
            };
        });
    };

    const handleTermChange = (value: Term) => () => {
        dispatch(setTerm(value));
        const selectedInstalment = value.instalments.find(
            (instalment) => instalment.name === instalmentType
        );
        const instalmentName = selectedInstalment
            ? selectedInstalment.name
            : value.instalments[0].name;
        const instalmentId = selectedInstalment ? selectedInstalment.id : value.instalments[0].id;
        dispatch(setInstalmentType(instalmentName as InstalmentType));
        dispatch(setInstalmentId(instalmentId));
        dispatch(updateBasketProductPaymentplanId(instalmentId));
    };

    const handleInstalmentChange = (value: string, id: string) => () => {
        dispatch(setInstalmentType(value as InstalmentType));
        dispatch(setInstalmentId(id));
        dispatch(updateBasketProductPaymentplanId(id));
    };

    const incrementStudentNumber = () => {
        if (numberOfStudents && numberOfStudents < maxStudentNumber) {
            const updatedNumberOfStudents = numberOfStudents ? numberOfStudents + 1 : 1;
            dispatch(setNumberOfStudents(updatedNumberOfStudents));

            if (updatedNumberOfStudents === 1) {
                dispatch(updateBasketRoommates([]));
            }
        }
    };

    const decrementStudentNumber = () => {
        if (numberOfStudents && numberOfStudents > minStudentNumber) {
            const updatedNumberOfStudents = numberOfStudents ? numberOfStudents - 1 : 1;
            dispatch(setNumberOfStudents(updatedNumberOfStudents));

            if (updatedNumberOfStudents === 1) {
                dispatch(updateBasketRoommates([]));
            }
        }
    };

    const getIconStyles = (studentsCount: number, type: 'increment' | 'decrement') => {
        if (type === 'increment') {
            return studentsCount < maxStudentNumber ? styles.activeIcon : styles.disabledIcon;
        }
        return studentsCount > minStudentNumber ? styles.activeIcon : styles.disabledIcon;
    };

    const getIconColor = (studentsCount: number, type: 'increment' | 'decrement') => {
        const activeColor = isHosTheme
            ? theme.palette.secondary.dark
            : theme.palette.text.secondary;
        const disabledColor = theme.palette.text.disabled;

        if (type === 'increment') {
            return studentsCount < maxStudentNumber ? activeColor : disabledColor;
        }
        return studentsCount > minStudentNumber ? activeColor : disabledColor;
    };

    if (!bookingDetails) return null;

    return (
        <Section header={t('booking_summary_booking_details_title')} withHeaderStipe={!isHosTheme}>
            <Box css={css(styles.wrapper, restStyles)}>
                <Box css={styles.studentNumber}>
                    <Typography variant="h4">
                        {t('booking_summary_booking_details_student_number_subtitle')}
                    </Typography>
                    <Box css={styles.counter}>
                        <Box
                            css={css(
                                styles.iconWrapper,
                                getIconStyles(numberOfStudents, 'decrement')
                            )}
                            onClick={decrementStudentNumber}
                        >
                            <MinusIcon color={getIconColor(numberOfStudents, 'decrement')} />
                        </Box>
                        <Typography variant="h4">{numberOfStudents}</Typography>
                        <Box
                            css={css(
                                styles.iconWrapper,
                                getIconStyles(numberOfStudents, 'increment')
                            )}
                            onClick={incrementStudentNumber}
                        >
                            <PlusIcon color={getIconColor(numberOfStudents, 'increment')} />
                        </Box>
                    </Box>
                </Box>
                <Box css={styles.bookingLengthWrapper}>
                    <Typography variant="h4">
                        {t('booking_summary_booking_details_length_subtitle')}
                    </Typography>
                    <Box css={styles.bookingLength}>
                        {bookingDetails.terms.map((term: Term) => (
                            <Select
                                key={term.name}
                                value={term.name}
                                isSelected={termName === term.name}
                                onClick={handleTermChange(term)}
                                title={getTermTitle(term.duration)}
                                subtitle={`${formatDate(term.startDate)} - ${formatDate(term.endDate)}`}
                            />
                        ))}
                    </Box>
                </Box>
                <Box css={styles.instalmentPlanWrapper}>
                    <Typography variant="h4">
                        {t('booking_summary_booking_details_instalment_plan_subtitle')}
                    </Typography>
                    <Box css={styles.instalmentPlan}>
                        {getInstalmentOptions().map(({ value, title, subtitle, id }) => (
                            <Select
                                key={value}
                                value={value}
                                isSelected={instalmentType === value}
                                onClick={handleInstalmentChange(value, id)}
                                title={title}
                                subtitle={subtitle}
                            />
                        ))}
                    </Box>
                </Box>
            </Box>
        </Section>
    );
};
